import { DateTime, IANAZone } from 'luxon';

export interface TimezoneOption {
  value: string;
  label: string;
}

/**
 * Whether the backend will accept this value as a time zone.
 *
 * Deliberately the same check the supplier service runs — luxon's
 * `IANAZone.isValidZone`, in
 * `supplierService/src/service/validator/schema/supplier.ts` — so the form never
 * blocks a value the backend would store, nor sends one it would reject. That
 * check delegates to `Intl`, so it is looser than "IANA name": it also allows a
 * fixed offset such as `+02:00`, and it is case-insensitive.
 *
 * `buildTimezoneOptions` only ever offers real zone names, which is what the
 * schema documents and what keeps cutoff times correct across DST. The stored
 * value is later parsed with `ZoneId.of()` to compute delivery dates, and that
 * throws on anything unrecognised.
 */
export const isValidTimezone = (value: string): boolean =>
  IANAZone.isValidZone(value);

/**
 * Every IANA zone the runtime knows, plus `currentValue` when it is a valid zone
 * the runtime omits.
 *
 * That last part matters: `Intl.supportedValuesOf` returns one canonical name
 * per zone, and which of a pair of aliases counts as canonical differs between
 * ICU versions (`Asia/Calcutta` vs `Asia/Kolkata`). A distributor saved with the
 * alias would otherwise show an empty picker and read as "no timezone set".
 */
const collectZones = (currentValue?: string | null): string[] => {
  const zones = new Set<string>(Intl.supportedValuesOf('timeZone'));

  if (currentValue && isValidTimezone(currentValue)) {
    zones.add(currentValue);
  }

  return Array.from(zones);
};

/**
 * The zone list for the distributor timezone picker, labelled with each zone's
 * current UTC offset and ordered west to east — the same shape the legacy admin
 * used, so CS reads a familiar list.
 *
 * Offsets are resolved once per zone rather than inside the sort comparator:
 * there are ~400 zones and the comparator runs far more often than that.
 */
export const buildTimezoneOptions = (
  currentValue?: string | null
): TimezoneOption[] => {
  const reference = DateTime.now();

  return collectZones(currentValue)
    .map((zone) => {
      const zoned = reference.setZone(zone);
      return {
        zone,
        offset: zoned.offset,
        offsetLabel: zoned.toFormat('ZZ'),
      };
    })
    .sort((a, b) => a.offset - b.offset || a.zone.localeCompare(b.zone))
    .map(({ zone, offsetLabel }) => ({
      value: zone,
      label: `(UTC${offsetLabel}) ${zone.replace(/_/g, ' ')}`,
    }));
};

/**
 * The zones matching what the operator typed.
 *
 * The picker has to filter its own list. HeroUI's Autocomplete only applies its
 * built-in filter to an uncontrolled collection: react-stately's
 * `useComboBoxState` reads `props.items != null || !defaultFilter ? collection :
 * filterCollection(...)`, so passing `items` opts out of filtering entirely and
 * typing does nothing. This field needs `items` because the list depends on the
 * stored zone, so the filtering comes back here.
 *
 * Matches the label and the raw IANA name, because the label spaces out
 * underscores: someone typing `New_York` means `America/New_York`, and someone
 * typing `Berlin` means the label.
 *
 * An empty query, or one that is already the full label of a chosen zone, returns
 * everything. Otherwise clicking a field that holds a value would offer only the
 * value it already holds.
 */
export const filterTimezoneOptions = (
  options: TimezoneOption[],
  query: string
): TimezoneOption[] => {
  const needle = query.trim().toLowerCase();

  if (!needle) return options;
  if (options.some((option) => option.label.toLowerCase() === needle)) {
    return options;
  }

  return options.filter(
    (option) =>
      option.label.toLowerCase().includes(needle) ||
      option.value.toLowerCase().includes(needle)
  );
};
